import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { API_CONFIG } from '../config/api';
import { Share2, Building2, CheckCircle, Search, ChevronDown } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import './Dashboard.css';

/** Set to `true` to show the "Share Credentials with a Tenant" card again. */
const SHOW_SHARE_CREDENTIALS_FORM = false;

function Share() {
  const { user, token, loading, refreshSharedCredentials } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sharedCredentials, setSharedCredentials] = useState([]);
  const [, setLoadingTenants] = useState(false);
  const [loadingShare, setLoadingShare] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  
  // Searchable select states
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTenants, setFilteredTenants] = useState([]);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const fetchTenants = useCallback(async () => {
    setLoadingTenants(true);
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/tenants`, {
        headers: { 'x-admin-key': 'local-admin-key' }
      });
      // Ensure we have an array
      const tenantsData = Array.isArray(response.data) ? response.data : (response.data.tenants || []);
      setTenants(tenantsData);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      setTenants([]); // Set empty array on error
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  const fetchSharedCredentials = useCallback(async () => {
    try {
      setLoadingCredentials(true);
      const response = await axios.get(`${API_CONFIG.BASE_URL}/user/shared-credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ensure we have an array
      const credentialsData = Array.isArray(response.data) ? response.data : (response.data.sharedCredentials || []);
      
      // Fetch additional user data for each credential to get QR codes
      const credentialsWithUserData = await Promise.all(
        credentialsData.map(async (cred) => {
          try {
            const userDataResponse = await axios.get(
              `${API_CONFIG.BASE_URL}/user-tenant-mapping/tenant/${cred.tenantId}/user/${cred.uniqueCode}`,
              {
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'x-api-key': cred.hmac_key,
                  'x-tenant-id': cred.tenantId
                }
              }
            );
            
            return {
              ...cred,
              userData: userDataResponse.data.user,
              tenantData: userDataResponse.data.tenant
            };
          } catch (error) {
            console.error(`Failed to fetch user data for ${cred.tenantName}:`, error);
            console.error('Error details:', {
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
              url: `${API_CONFIG.BASE_URL}/user-tenant-mapping/tenant/${cred.tenantId}/user/${cred.uniqueCode}`
            });
            
            let errorMessage = 'Failed to load user data';
            if (error.response?.status === 404) {
              errorMessage = 'User mapping not found';
            } else if (error.response?.status === 401) {
              errorMessage = 'Unauthorized access';
            } else if (error.response?.status === 403) {
              errorMessage = 'Access forbidden';
            } else if (error.response?.status >= 500) {
              errorMessage = 'Server error';
            } else if (error.code === 'NETWORK_ERROR') {
              errorMessage = 'Network error';
            }
            
            return {
              ...cred,
              userData: null,
              tenantData: null,
              error: errorMessage,
              errorDetails: {
                status: error.response?.status,
                message: error.response?.data?.message || error.message
              }
            };
          }
        })
      );
      
      setSharedCredentials(credentialsWithUserData);
    } catch (error) {
      console.error('Failed to fetch shared credentials:', error);
      setSharedCredentials([]); // Set empty array on error
    } finally {
      setLoadingCredentials(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    if (SHOW_SHARE_CREDENTIALS_FORM) {
      fetchTenants();
    }
    fetchSharedCredentials();
  }, [token, fetchTenants, fetchSharedCredentials]);

  // Filter tenants based on search term
  useEffect(() => {
    if (!Array.isArray(tenants)) {
      setFilteredTenants([]);
      return;
    }
    
    if (searchTerm.trim() === '') {
      setFilteredTenants(tenants);
    } else {
      const filtered = tenants.filter(tenant => 
        (tenant.displayName || tenant.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.tenantId.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTenants(filtered);
    }
  }, [tenants, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  const handleShareCredentials = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) {
      setShareMessage('Please select a tenant to share with.');
      return;
    }

    setLoadingShare(true);
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/user/share`, {
        tenantId: selectedTenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShareMessage(`✅ Credentials shared successfully! Unique code: ${response.data.uniqueCode}`);
      setSelectedTenantId('');
      
      // Refresh shared credentials
      await fetchSharedCredentials();
      refreshSharedCredentials();
    } catch (error) {
      console.error('Failed to share credentials:', error);
      setShareMessage('❌ Failed to share credentials');
    } finally {
      setLoadingShare(false);
    }
  };

  const handleUnshare = async (mappingId, tenantName) => {
    if (!window.confirm(`Are you sure you want to unshare credentials with ${tenantName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/user/unshare/${mappingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShareMessage(`✅ Credentials unshared successfully from ${tenantName}`);
      
      // Refresh shared credentials
      await fetchSharedCredentials();
      refreshSharedCredentials();
    } catch (error) {
      console.error('Failed to unshare credentials:', error);
      setShareMessage('❌ Failed to unshare credentials');
    }
  };

  // Searchable select handlers
  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenantId(tenant.tenantId);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setSearchTerm('');
    }
  };

  const selectedTenant = tenants.find(t => t.tenantId === selectedTenantId);

  return (
    <DashboardLayout activeNav="share">
        {SHOW_SHARE_CREDENTIALS_FORM && (
        <div className="dashboard-section">
          <h2>
            <Share2 size={24} />
            Share Credentials with a Tenant
          </h2>
          
          <form onSubmit={handleShareCredentials} className="share-form">
            <div className="form-group">
              <label htmlFor="tenantSelect">Select Tenant:</label>
              <div className="searchable-select" ref={dropdownRef}>
                <div 
                  className={`select-trigger ${isDropdownOpen ? 'open' : ''}`}
                  onClick={handleDropdownToggle}
                >
                  <span className="select-value">
                    {selectedTenant ? (selectedTenant.displayName || selectedTenant.name) : 'Choose a tenant...'}
                  </span>
                  <ChevronDown size={20} className="chevron" />
                </div>
                
                {isDropdownOpen && (
                  <div className="select-dropdown">
                    <div className="search-input-container">
                      <Search size={16} className="search-icon" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search tenants..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        className="search-input"
                      />
                    </div>
                    <div className="select-options">
                      {filteredTenants.length === 0 ? (
                        <div className="no-options">No tenants found</div>
                      ) : (
                        Array.isArray(filteredTenants) && filteredTenants.map(tenant => (
                          <div
                            key={tenant.tenantId}
                            className={`select-option ${selectedTenantId === tenant.tenantId ? 'selected' : ''}`}
                            onClick={() => handleTenantSelect(tenant)}
                          >
                            <div className="option-content">
                              <div className="option-name">{tenant.displayName || tenant.name}</div>
                              <div className="option-id">ID: {tenant.tenantId}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {selectedTenant && (
              <div className="tenant-info">
                <h3>
                  <Building2 className="icon" />
                  {selectedTenant.displayName || selectedTenant.name}
                </h3>
                <p><strong>Tenant ID:</strong> {selectedTenant.tenantId}</p>
                <p><strong>Status:</strong> {selectedTenant.status}</p>
                <p><strong>Users:</strong> {selectedTenant.userCount}</p>
              </div>
            )}

            <button 
              type="submit" 
              disabled={!selectedTenantId || loadingShare}
              className="btn-primary"
            >
              {loadingShare ? 'Sharing...' : 'Share Credentials'}
            </button>
          </form>

          {shareMessage && (
            <div className={`message ${shareMessage.includes('✅') ? 'success' : 'error'}`}>
              {shareMessage}
            </div>
          )}
        </div>
        )}

        {/* Shared Credentials */}
        <div className="dashboard-section">
          <h2>
            <CheckCircle size={24} />
            Shared Credentials
          </h2>
          
          {loadingCredentials ? (
            <p>Loading credentials...</p>
          ) : sharedCredentials.length === 0 ? (
            <p>No credentials shared yet.</p>
          ) : (
            <div className="credentials-grid">
              {Array.isArray(sharedCredentials) && sharedCredentials.map((cred, index) => (
                <div key={index} className="credential-card">
                  <div className="credential-info">
                    <h3>{cred.tenantName}</h3>
                    <p><strong>Unique Code:</strong> <code>{cred.uniqueCode}</code></p>
                    <p><strong>Shared on:</strong> {new Date(cred.sharedAt).toLocaleString()}</p>
                    <p><strong>Status:</strong> <span className={`status ${cred.status}`}>{cred.status}</span></p>
                    
                    {cred.error && (
                      <div className="error-message">
                        <p>❌ {cred.error}</p>
                        {cred.errorDetails && (
                          <details className="error-details">
                            <summary>Error Details</summary>
                            <p><strong>Status:</strong> {cred.errorDetails.status}</p>
                            <p><strong>Message:</strong> {cred.errorDetails.message}</p>
                            <p><strong>URL:</strong> <code>user-tenant-mapping/tenant/{cred.tenantId}/user/{cred.uniqueCode}</code></p>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {cred.userData && cred.userData.qrBlob && (
                    <div className="qr-code-section">
                      <h4>QR Code</h4>
                      <div className="qr-code-container">
                        <img 
                          src={cred.userData.qrBlob} 
                          alt={`QR Code for ${cred.userData.fullName}`}
                          className="qr-code-image"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="credential-actions">
                    <button
                      onClick={() => handleUnshare(cred.mappingId, cred.tenantName)}
                      className="unshare-button"
                    >
                      Unshare
                    </button>
                    {cred.redirection_url ? (
                      <a
                        href={cred.redirection_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="next-link"
                      >
                        Next →
                      </a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
    </DashboardLayout>
  );
}

export default Share;
